import type { Card } from "../CardFactory";
import { PiAsset } from "../Types/PiAsset";
import { useAppDispatch } from '../app/hooks'
import { addCard, removeCard } from "../Slices/DashboardSlice";
import { ActionMenu } from "./Generic/SideMenu/ActionMenu";
import { TreeView } from "./Generic/SideMenu/TreeView";
import { usePersistedOpenItems } from "./Generic/SideMenu/usePersistedOpenItems";
import { formatAssetLabel, getAssetChildren, isAssetMuted } from "./AssetTreeShared";
import { DASHBOARD_ASSET_TREE_STORAGE_KEY, dashboardAssetId, getDashboardAssetActions } from "./dashboardAssetMenu";

interface DashboardAssetMenuProps {
    asset: PiAsset;
    cards: Card[];
}

/** "..." menu of an asset in the Dashboard drawer: adds or removes its cards. */
export function DashboardAssetMenu({ asset, cards }: DashboardAssetMenuProps) {
    const dispatch = useAppDispatch();

    return (
        <ActionMenu
            ariaLabel={`More options for ${formatAssetLabel(asset)}`}
            items={getDashboardAssetActions(asset, cards).map((action) => ({
                label: action.label,
                disabled: action.disabled,
                onClick: () => {
                    if (action.add) {
                        dispatch(addCard(action.add));
                    } else if (action.remove) {
                        dispatch(removeCard(action.remove));
                    }
                },
            }))}
        />
    );
}

interface AssetTreeProps {
    asset: PiAsset;
    cards: Card[];
}

export function AssetTree({ asset, cards }: AssetTreeProps) {
    const [openItems, setOpenItems] = usePersistedOpenItems(DASHBOARD_ASSET_TREE_STORAGE_KEY, asset.Name);

    return (
        <TreeView<PiAsset>
            items={[asset]}
            ariaLabel="Asset tree"
            getId={dashboardAssetId}
            getChildren={getAssetChildren}
            renderLabel={formatAssetLabel}
            isMuted={isAssetMuted}
            renderActions={(item, _parent, depth) => (depth === 0 ? null : <DashboardAssetMenu asset={item} cards={cards} />)}
            openItems={openItems}
            onOpenChange={setOpenItems}
        />
    );
}
